import React from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  BellIcon,
  CirclesDeco,
  LockIcon,
  MessageIcon,
  RadarChart,
  RadialProgress,
} from "../../assets/icons/icons";
import teamTrustLogo from "../../assets/images/team-trust-logo.png";
import Field from "../../components/common/Field";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { loginCoach, loginUser } from "./store/login/thunk";
import useOrganization from "../organization/hooks/useOrganization";
import { loginValidationSchema } from "./validations/login.validation";
import { useProfile } from "./hooks/useProfile";

const Login: React.FC = () => {
  document.title = `Login - ${process.env.REACT_APP_NAME}`;
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { loading } = useProfile();
  const { isCoachDomain } = useOrganization();

  // const [rememberMe, setRememberMe] = useState(false);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: loginValidationSchema,
    onSubmit: (values) => {
      if (isCoachDomain) {
        dispatch(loginCoach(values, navigate));
      } else {
        dispatch(loginUser(values, navigate));
      }
    },
  });

  const { currentOrganization } = useOrganization();

  return (
    <div className="login-page">
      <div className="login-left">
        <header className="create-account-header">
          <img
            src={currentOrganization?.image ?? teamTrustLogo}
            alt="team trust logo"
            className="login-logo"
          />
        </header>

        <div className="login-body">
          <div className="login-card max-w-[444px]">
            <h1 className="login-title">Login</h1>
            <p className="login-description">
              This is the place where individual insights will help your team
              win together, faster and easier.
            </p>

            <form
              onSubmit={formik.handleSubmit}
              className="flex flex-col gap-5 max-w-[315px]"
            >
              <Field label="Email">
                <input
                  id="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Email Address"
                  className="login-input"
                />
                {formik.touched.email && formik.errors.email && (
                  <span className="form-error">{formik.errors.email}</span>
                )}
              </Field>

              <Field label="Password">
                <input
                  id="password"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Password"
                  className="login-input"
                />
                {formik.touched.password && formik.errors.password && (
                  <span className="form-error">{formik.errors.password}</span>
                )}
              </Field>

              <div className="flex items-center justify-between">
                {/* <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="login-checkbox"
                  />
                  <span className="login-remember-text">Remember Me</span>
                </label> */}
               <Link to="/forgot-password" className="login-forgot">
                  Forgot Password?
               </Link>
              </div>

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? "Logging in..." : "Log In"}
              </button>
            </form>

            <div className="login-footer-links">
              <ul className="login-footer-list">
                <li>
                  <Link to="/privacy-policy" className="hover:text-[#4B5DD4] transition">
                    Privacy Policy
                  </Link>
                </li>
                <li className="mx-2.5 w-1.5 h-1.5 rounded-full bg-[#7B9DED] flex-shrink-0" />
                <li>
                  <Link
                    to="/communication-usage-terms"
                    className="hover:text-[#4B5DD4] transition hover:no-underline"
                  >
                    Communication &amp; Usage Terms
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="login-info">
            <h2 className="login-info-title">
              Build winning teams,
              <br />
              faster and easier.
            </h2>
            <p className="login-info-text">Thrive and win together.</p>
          </div>
        </div>

        <footer className="login-desktop-footer">
          <ul className="flex flex-wrap items-center gap-0 text-xs text-slate-500">
            <li>
              <Link to="/privacy-policy" className="hover:text-[#4B5DD4] transition">
                Privacy Policy
              </Link>
            </li>
            <li className="mx-2.5 w-1.5 h-1.5 rounded-full bg-[#7B9DED] flex-shrink-0" />
            <li>
              <Link
                to="/communication-usage-terms"
                className="hover:text-[#4B5DD4] transition hover:no-underline"
              >
                Communication &amp; Usage Terms
              </Link>
            </li>
          </ul>
        </footer>
      </div>

      {/* ── RIGHT PANEL ───────────────────────────────────────────────────── */}

      <div className="login-right">
        <CirclesDeco />

        <div
          className="relative mx-auto w-full px-4 py-11"
          style={{ maxWidth: 786 }}
        >
          <div
            className="relative mx-auto"
            style={{ maxWidth: 525, paddingTop: 77 }}
          >
            <div
              className="absolute flex items-center justify-center rounded-full bg-white z-10"
              style={{
                top: 0,
                left: 147,
                width: 70,
                height: 70,
                fontSize: 70,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <MessageIcon className="text-dark-blue" />
            </div>

            <div
              className="absolute flex items-center justify-center rounded-full bg-white z-10"
              style={{
                top: 107,
                left: -15,
                width: 101,
                height: 101,
                fontSize: 101,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <BellIcon className="text-dark-blue" />
            </div>

            <div
              className="absolute flex items-center justify-center rounded-full bg-white z-10"
              style={{
                bottom: 13,
                right: 59,
                width: 72,
                height: 72,
                fontSize: 72,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <LockIcon className="text-dark-blue" />
            </div>

            <div
              style={{
                position: "relative",
                marginLeft: "auto",
                maxWidth: 272,
                zIndex: 3,
                paddingBottom: 49,
              }}
            >
              <RadialProgress />
            </div>

            <div
              className="relative bg-white"
              style={{
                marginTop: -92,
                maxWidth: 301,
                zIndex: 2,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  paddingTop: `${(233 / 301) * 100}%`,
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center p-3">
                  <RadarChart />
                </div>
              </div>
            </div>
          </div>

          <div
            className="relative mx-auto text-center px-4"
            style={{ marginTop: 77, maxWidth: 400, zIndex: 1 }}
          >
            <h2
              className="font-bold text-dark-blue leading-snug mb-6"
              style={{ fontSize: 35, fontFamily: "var(--font-primary)" }}
            >
              Build winning teams,
              <br />
              faster and easier.
            </h2>
            <p
              className="text-dark-blue"
              style={{ fontSize: 18, fontFamily: "var(--font-primary)" }}
            >
              Thrive and win together.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
